import { useState, useEffect } from 'react';
import { Card } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { CheckCircle2, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const checks = [
  { id: 'income', label: 'التحقق من الاهلية الائتمانية', logo: '/images/logos/simah.jpg', source: 'سمة' },
  { id: 'job', label: 'التحقق من الحالة الوظيفية', logo: '/images/logos/gosi.png', source: 'التأمينات الاجتماعية' },
  { id: 'health', label: 'التحقق من الحالة الصحية (نفيس)', logo: '/images/logos/nefis.svg', source: 'وزارة الصحة' },
  { id: 'molim', label: 'التحقق من السجل العقاري', logo: '/images/logos/rer.png', source: 'الهيئة العامة للعقار' },
];

// Mock profile data that would come from external services
const getMockUserProfile = () => ({
  idInformation: {
    fullName: 'فيصل سعود الخميسي',
    dateOfBirth: '1990-05-15',
    placeOfBirth: 'الرياض',
    socialStatus: 'married',
    nationality: 'سعودي',
    numberOfDependants: 3,
    idType: 'هوية وطنية',
    idIssueDate: '2020-01-15',
    idExpiryDate: '2030-01-15',
    idIssuePlace: 'الرياض',
    nationalAddress: 'الرياض - حي النخيل - شارع الملك فهد - مبنى 123',
  },
  jobInformation: {
    jobTitle: 'مهندس برمجيات',
    companyName: 'شركة التقنية المتقدمة',
    joiningDate: '2018-03-01',
    firstSalaryDate: '2018-04-25',
    yearsAtCurrentJob: 6,
    totalExperienceYears: 10,
    salaryDayOfMonth: 25,
  },
  otherLoans: {
    personalLoan: 50000,
    realEstateLoan: 0,
  },
  contactInformation: {
    phoneNumber: '0512345678',
    email: 'faisal@email.com',
  },
  emergencyContact: {
    name: '',
    phoneNumber: '',
    email: '',
  },
  monthlyExpenses: {
    invoices: 0,
    rent: 0,
    workersSalary: 0,
    transportation: 0,
    education: 0,
    health: 0,
    insurance: 0,
  },
});

export default function EligibilityCheck({ onComplete }) {
  const [currentCheck, setCurrentCheck] = useState(0);
  const [completedChecks, setCompletedChecks] = useState([]);
  const [isBankCheck, setIsBankCheck] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    if (currentCheck < checks.length) {
      const timer = setTimeout(() => {
        setCompletedChecks([...completedChecks, checks[currentCheck].id]);
        setCurrentCheck(currentCheck + 1);
      }, 1500);
      return () => clearTimeout(timer);
    } else if (!isBankCheck) {
      setIsBankCheck(true);
    }
  }, [currentCheck, completedChecks, isBankCheck]);

  useEffect(() => {
    if (isBankCheck) {
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isBankCheck]);

  const handleComplete = () => {
    // Return the mock profile data collected from external services
    const profile = getMockUserProfile();
    onComplete(profile);
  };

  const progress = isBankCheck ? 100 : (completedChecks.length / checks.length) * 70;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100"
    >
      <Card className="w-full max-w-lg p-8 border-0 shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">جاري فحص الأهلية</h1>
          <p className="text-slate-500">يرجى الانتظار بينما نتحقق من بياناتك</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-sm text-slate-500 text-center mt-2">{Math.round(progress)}%</p>
        </div>

        {/* Checks List */}
        <div className="space-y-3 mb-8">
          {checks.map((check, index) => {
            const isCompleted = completedChecks.includes(check.id);
            const isActive = currentCheck === index && !isCompleted;

            return (
              <motion.div
                key={check.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                  isCompleted ? 'bg-emerald-50' : isActive ? 'bg-slate-50' : 'bg-slate-50/50'
                }`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-white shadow-sm">
                  <img src={check.logo} alt={check.label} className="w-8 h-8 object-contain" />
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${isCompleted ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {check.label}
                  </p>
                  <p className="text-sm text-slate-400">{check.source}</p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center">
                  {isCompleted ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  ) : isActive ? (
                    <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                  ) : null}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bank Check */}
        <AnimatePresence>
          {isBankCheck && !showButton && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100"
            >
              <div className="flex items-center gap-4">
                <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                <div>
                  <p className="font-semibold text-amber-800">جاري جمع البيانات</p>
                  <p className="text-sm text-amber-600">التحقق من جميع المصادر...</p>
                </div>
              </div>
            </motion.div>
          )}
          {showButton && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Button
                onClick={handleComplete}
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium"
              >
                مراجعة البيانات
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
}
